import React, { useState, useEffect, useMemo } from 'react';
import { Github, Plus, Settings, Trash2, Play, Eye, AlertTriangle, CheckCircle, Clock, BarChart3, Shield, X, FileCode, GitBranch, ChevronDown, ChevronRight, ExternalLink, AlertCircle, Info, Filter, SortDesc, SortAsc, Layers, Database, Lock, Code, Download, FileText, Check, Square, CheckSquare } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const GitHubScanner = ({ api, user, onNavigateToProfile }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [githubOrgs, setGithubOrgs] = useState([]);
  const [scanResults, setScanResults] = useState([]);
  const [scanJobs, setScanJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState('');

  // Scan detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [scanDetail, setScanDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [expandedRepos, setExpandedRepos] = useState({});

  // Filtering and sorting state for detail modal
  const [detailView, setDetailView] = useState('all'); // 'all', 'mcp', 'pii', 'compliance', 'repos'
  const [detailFilter, setDetailFilter] = useState([]); // Detection type filters
  const [detailSort, setDetailSort] = useState('confidence-desc'); // 'confidence-desc', 'confidence-asc', 'type', 'repo'

  // Pagination state
  const [complianceLimit, setComplianceLimit] = useState(50);
  const [detectionsLimit, setDetectionsLimit] = useState(50);
  const [expandedRepoFindings, setExpandedRepoFindings] = useState({}); // Track which repos show all findings

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSelections, setExportSelections] = useState({
    repos: {},
    detections: {},
    compliance: {}
  });
  const [selectAllRepos, setSelectAllRepos] = useState(true);
  const [selectAllDetections, setSelectAllDetections] = useState(true);
  const [selectAllCompliance, setSelectAllCompliance] = useState(true);

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadGithubOrganizations(),
        loadScanJobs(),
        loadScanResults()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGithubOrganizations = async () => {
    try {
      const response = await api.getGithubOrganizations();
      setGithubOrgs(response.organizations || []);
    } catch (error) {
      console.error('Error loading GitHub organizations:', error);
      setGithubOrgs([]);
    }
  };

  const loadScanJobs = async () => {
    try {
      const response = await api.getGitHubScanJobs();
      setScanJobs(response.jobs || []);
    } catch (error) {
      console.error('Error loading scan jobs:', error);
      setScanJobs([]);
    }
  };

  const loadScanResults = async (organization = null) => {
    try {
      if (organization || selectedOrg) {
        const response = await api.getGitHubScanResults(organization || selectedOrg);
        setScanResults(response.scans || []);
      }
    } catch (error) {
      console.error('Error loading scan results:', error);
      setScanResults([]);
    }
  };

  const handleManualScan = async (organizationUrl) => {
    try {
      setLoading(true);
      // Extract organization name from URL for the API
      const orgName = extractOrgNameFromUrl(organizationUrl);
      const response = await api.triggerGitHubScan(orgName);

      // Start polling for scan completion
      if (response.job_id) {
        pollForScanCompletion(response.job_id, orgName);
      }

      // Refresh jobs list immediately
      setTimeout(() => loadScanJobs(), 500);
    } catch (error) {
      alert(`Error triggering scan: ${error.message}`);
      setLoading(false);
    }
  };

  // Poll for scan completion and auto-refresh UI
  const pollForScanCompletion = (jobId, orgName) => {
    let pollCount = 0;
    const maxPolls = 60; // Max 2 minutes of polling (2s intervals)

    const pollInterval = setInterval(async () => {
      pollCount++;

      try {
        const response = await api.getGitHubScanJobs(null, 5);
        const job = response.jobs?.find(j => j.job_id === jobId);

        if (job) {
          if (job.status === 'completed') {
            clearInterval(pollInterval);
            setLoading(false);

            // Refresh all data
            await loadScanJobs();
            await loadScanResults(orgName);

            // Show success notification
            const detections = job.results?.detections_count || 0;
            const repos = job.results?.repositories_scanned || 0;
            alert(`Scan completed!\n\n${repos} repositories scanned\n${detections} MCP detections found`);

            // Auto-open detail modal if there are detections
            if (job.results?.scan_id && detections > 0) {
              openDetailModal(job.results.scan_id);
            }
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            setLoading(false);
            alert(`Scan failed: ${job.error || 'Unknown error'}`);
            await loadScanJobs();
          }
        }

        // Stop polling after max attempts
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          setLoading(false);
          await loadScanJobs();
        }
      } catch (error) {
        console.error('Error polling scan status:', error);
      }
    }, 2000); // Poll every 2 seconds
  };

  const extractOrgNameFromUrl = (url) => {
    // Extract organization name from URL
    if (url.includes('github.com/')) {
      const parts = url.replace(/\/$/, '').split('/');
      return parts[parts.length - 1];
    }
    return url; // Fallback if it's already just the org name
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="status-icon status-completed" />;
      case 'failed':
        return <AlertTriangle size={16} className="status-icon status-failed" />;
      case 'running':
        return <Clock size={16} className="status-icon status-running" />;
      default:
        return <Clock size={16} className="status-icon status-pending" />;
    }
  };

  // Load detailed scan results
  const loadScanDetail = async (scanId) => {
    try {
      setLoadingDetail(true);
      const detail = await api.getGitHubScanDetail(scanId);
      setScanDetail(detail);
      setShowDetailModal(true);
      setExpandedRepos({});
    } catch (error) {
      console.error('Error loading scan detail:', error);
      alert(`Error loading scan details: ${error.message}`);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Toggle repository expansion
  const toggleRepoExpansion = (repoName) => {
    setExpandedRepos(prev => ({
      ...prev,
      [repoName]: !prev[repoName]
    }));
  };

  // Get confidence badge style
  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.7) return { className: 'confidence-high', label: 'High' };
    if (confidence >= 0.4) return { className: 'confidence-medium', label: 'Medium' };
    return { className: 'confidence-low', label: 'Low' };
  };

  // Get all detections flattened from repositories
  const getAllDetections = useMemo(() => {
    if (!scanDetail?.repositories) return [];
    const allDetections = [];
    scanDetail.repositories.forEach(repo => {
      (repo.mcp_detections || []).forEach(d => {
        allDetections.push({ ...d, repository: repo.full_name, category: 'mcp' });
      });
    });
    return allDetections;
  }, [scanDetail]);

  // Get PII-specific detections
  const getPIIDetections = useMemo(() => {
    return getAllDetections.filter(d => d.type === 'pii_handling');
  }, [getAllDetections]);

  // Get MCP-specific detections (excluding PII)
  const getMCPDetections = useMemo(() => {
    return getAllDetections.filter(d => d.type !== 'pii_handling');
  }, [getAllDetections]);

  // Get all compliance findings flattened
  const getAllCompliance = useMemo(() => {
    if (!scanDetail?.repositories) return [];
    const allFindings = [];
    scanDetail.repositories.forEach(repo => {
      (repo.compliance_findings || []).forEach(f => {
        allFindings.push({ ...f, repository: repo.full_name, category: 'compliance' });
      });
    });
    return allFindings;
  }, [scanDetail]);

  // Get unique detection types for filtering
  const getDetectionTypes = useMemo(() => {
    const types = new Set(getAllDetections.map(d => d.type));
    return Array.from(types);
  }, [getAllDetections]);

  // Filter and sort detections based on current view and settings
  const getFilteredDetections = useMemo(() => {
    let detections = [];

    if (detailView === 'mcp') {
      detections = getMCPDetections;
    } else if (detailView === 'pii') {
      detections = getPIIDetections;
    } else if (detailView === 'all' || detailView === 'repos') {
      detections = getAllDetections;
    }

    // Apply type filters
    if (detailFilter.length > 0) {
      detections = detections.filter(d => detailFilter.includes(d.type));
    }

    // Apply sorting
    detections = [...detections].sort((a, b) => {
      switch (detailSort) {
        case 'confidence-desc':
          return b.confidence - a.confidence;
        case 'confidence-asc':
          return a.confidence - b.confidence;
        case 'type':
          return a.type.localeCompare(b.type);
        case 'repo':
          return a.repository.localeCompare(b.repository);
        default:
          return b.confidence - a.confidence;
      }
    });

    return detections;
  }, [detailView, detailFilter, detailSort, getAllDetections, getMCPDetections, getPIIDetections]);

  // Filter and sort compliance findings
  const getFilteredCompliance = useMemo(() => {
    let findings = getAllCompliance;

    // Apply sorting
    findings = [...findings].sort((a, b) => {
      switch (detailSort) {
        case 'confidence-desc':
          return b.confidence - a.confidence;
        case 'confidence-asc':
          return a.confidence - b.confidence;
        case 'type':
          return a.type.localeCompare(b.type);
        case 'repo':
          return a.repository.localeCompare(b.repository);
        default:
          return b.confidence - a.confidence;
      }
    });

    return findings;
  }, [detailSort, getAllCompliance]);

  // Get sorted repositories
  const getSortedRepos = useMemo(() => {
    if (!scanDetail?.repositories) return [];

    return [...scanDetail.repositories].sort((a, b) => {
      switch (detailSort) {
        case 'confidence-desc':
          const aMaxConf = Math.max(
            ...(a.mcp_detections || []).map(d => d.confidence),
            ...(a.compliance_findings || []).map(f => f.confidence),
            0
          );
          const bMaxConf = Math.max(
            ...(b.mcp_detections || []).map(d => d.confidence),
            ...(b.compliance_findings || []).map(f => f.confidence),
            0
          );
          return bMaxConf - aMaxConf;
        case 'confidence-asc':
          const aMinConf = Math.min(
            ...(a.mcp_detections || []).map(d => d.confidence),
            ...(a.compliance_findings || []).map(f => f.confidence),
            1
          );
          const bMinConf = Math.min(
            ...(b.mcp_detections || []).map(d => d.confidence),
            ...(b.compliance_findings || []).map(f => f.confidence),
            1
          );
          return aMinConf - bMinConf;
        case 'type':
          return (b.mcp_detections_count + b.compliance_findings_count) - (a.mcp_detections_count + a.compliance_findings_count);
        case 'repo':
          return a.name.localeCompare(b.name);
        default:
          return (b.mcp_detections_count + b.compliance_findings_count) - (a.mcp_detections_count + a.compliance_findings_count);
      }
    });
  }, [scanDetail, detailSort]);

  // Reset filters when modal opens
  const openDetailModal = async (scanId) => {
    setDetailView('all');
    setDetailFilter([]);
    setDetailSort('confidence-desc');
    setExpandedRepos({});
    setComplianceLimit(50);
    setDetectionsLimit(50);
    setExpandedRepoFindings({});
    setShowExportModal(false);
    setExportSelections({ repos: {}, detections: {}, compliance: {} });
    setSelectAllRepos(true);
    setSelectAllDetections(true);
    setSelectAllCompliance(true);
    await loadScanDetail(scanId);
  };

  // Toggle expanded repo findings
  const toggleRepoFindingsExpand = (repoName) => {
    setExpandedRepoFindings(prev => ({
      ...prev,
      [repoName]: !prev[repoName]
    }));
  };

  // Initialize export selections when scan detail loads
  const initializeExportSelections = () => {
    if (!scanDetail) return;

    const repos = {};
    const detections = {};
    const compliance = {};

    scanDetail.repositories?.forEach((repo, idx) => {
      repos[repo.full_name] = true;
      repo.mcp_detections?.forEach((d, dIdx) => {
        detections[`${repo.full_name}-${dIdx}`] = true;
      });
      repo.compliance_findings?.forEach((f, fIdx) => {
        compliance[`${repo.full_name}-${fIdx}`] = true;
      });
    });

    setExportSelections({ repos, detections, compliance });
  };

  // Toggle selection
  const toggleSelection = (category, key) => {
    setExportSelections(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: !prev[category][key]
      }
    }));
  };

  // Toggle all in category
  const toggleAllInCategory = (category, value) => {
    const newSelections = { ...exportSelections[category] };
    Object.keys(newSelections).forEach(key => {
      newSelections[key] = value;
    });
    setExportSelections(prev => ({
      ...prev,
      [category]: newSelections
    }));
  };

  // Generate PDF report
  const generatePDFReport = () => {
    if (!scanDetail) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Title
    doc.setFontSize(20);
    doc.setTextColor(102, 126, 234);
    doc.text('GitHub Security Scan Report', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Organization and date
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Organization: ${scanDetail.organization}`, 20, yPos);
    yPos += 8;
    doc.text(`Scan Date: ${new Date(scanDetail.started_at).toLocaleString()}`, 20, yPos);
    yPos += 8;
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, yPos);
    yPos += 15;

    // Summary
    doc.setFontSize(14);
    doc.setTextColor(102, 126, 234);
    doc.text('Summary', 20, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const selectedRepos = Object.values(exportSelections.repos).filter(v => v).length;
    const selectedDetections = Object.values(exportSelections.detections).filter(v => v).length;
    const selectedCompliance = Object.values(exportSelections.compliance).filter(v => v).length;

    doc.text(`Repositories: ${selectedRepos} of ${scanDetail.repositories_scanned}`, 20, yPos);
    yPos += 6;
    doc.text(`MCP Detections: ${selectedDetections}`, 20, yPos);
    yPos += 6;
    doc.text(`Compliance Findings: ${selectedCompliance}`, 20, yPos);
    yPos += 15;

    // Selected Repositories
    const selectedRepoNames = Object.entries(exportSelections.repos)
      .filter(([_, selected]) => selected)
      .map(([name]) => name);

    if (selectedRepoNames.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(102, 126, 234);
      doc.text('Repositories', 20, yPos);
      yPos += 10;

      selectedRepoNames.forEach(repoName => {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(`• ${repoName}`, 25, yPos);
        yPos += 6;
      });
      yPos += 10;
    }

    // MCP Detections Table
    const detectionsData = [];
    scanDetail.repositories?.forEach(repo => {
      repo.mcp_detections?.forEach((d, idx) => {
        const key = `${repo.full_name}-${idx}`;
        if (exportSelections.detections[key]) {
          detectionsData.push([
            repo.name,
            d.type.replace(/_/g, ' '),
            d.file_path,
            `${Math.round(d.confidence * 100)}%`,
            d.confidence >= 0.7 ? 'High' : d.confidence >= 0.4 ? 'Medium' : 'Low'
          ]);
        }
      });
    });

    if (detectionsData.length > 0) {
      if (yPos > 200) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(102, 126, 234);
      doc.text('MCP Detections', 20, yPos);
      yPos += 5;

      doc.autoTable({
        startY: yPos,
        head: [['Repository', 'Type', 'File', 'Confidence', 'Severity']],
        body: detectionsData,
        theme: 'striped',
        headStyles: { fillColor: [102, 126, 234] },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 35 },
          2: { cellWidth: 60 },
          3: { cellWidth: 20 },
          4: { cellWidth: 20 }
        }
      });
      yPos = doc.lastAutoTable.finalY + 15;
    }

    // Compliance Findings Table
    const complianceData = [];
    scanDetail.repositories?.forEach(repo => {
      repo.compliance_findings?.forEach((f, idx) => {
        const key = `${repo.full_name}-${idx}`;
        if (exportSelections.compliance[key]) {
          complianceData.push([
            repo.name,
            f.control_id,
            f.type.replace(/_/g, ' '),
            f.compliance_status?.replace(/_/g, ' ') || 'N/A',
            `${Math.round(f.confidence * 100)}%`
          ]);
        }
      });
    });

    if (complianceData.length > 0) {
      if (yPos > 200) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(102, 126, 234);
      doc.text('ISO 42001 Compliance Findings', 20, yPos);
      yPos += 5;

      doc.autoTable({
        startY: yPos,
        head: [['Repository', 'Control ID', 'Type', 'Status', 'Confidence']],
        body: complianceData.slice(0, 100), // Limit to 100 for PDF size
        theme: 'striped',
        headStyles: { fillColor: [102, 126, 234] },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 25 },
          2: { cellWidth: 50 },
          3: { cellWidth: 35 },
          4: { cellWidth: 25 }
        }
      });

      if (complianceData.length > 100) {
        yPos = doc.lastAutoTable.finalY + 5;
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Note: Showing 100 of ${complianceData.length} compliance findings`, 20, yPos);
      }
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Agentic Threat Modeler - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Save the PDF
    const filename = `scan-report-${scanDetail.organization}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
    setShowExportModal(false);
  };

  // Open export modal and initialize selections
  const openExportModal = () => {
    initializeExportSelections();
    setShowExportModal(true);
  };

  // Render the scan detail modal
  const renderDetailModal = () => {
    if (!showDetailModal || !scanDetail) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
        <div className="scan-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">
              <Github size={24} />
              <div>
                <h2>Scan Results: {scanDetail.organization}</h2>
                <span className="scan-date">
                  {new Date(scanDetail.started_at).toLocaleString()}
                </span>
              </div>
            </div>
            <div className="modal-header-actions">
              <button className="export-btn" onClick={openExportModal}>
                <Download size={18} />
                Export PDF
              </button>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Clickable Summary Statistics */}
          <div className="detail-summary clickable-summary">
            <div
              className={`summary-card clickable ${detailView === 'repos' ? 'active' : ''}`}
              onClick={() => setDetailView('repos')}
            >
              <div className="summary-icon repos">
                <GitBranch size={20} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{scanDetail.repositories_scanned}</div>
                <div className="summary-label">Repositories</div>
              </div>
            </div>
            <div
              className={`summary-card clickable ${detailView === 'mcp' ? 'active' : ''}`}
              onClick={() => setDetailView('mcp')}
            >
              <div className="summary-icon detections">
                <Code size={20} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{getMCPDetections.length}</div>
                <div className="summary-label">MCP Detections</div>
              </div>
            </div>
            <div
              className={`summary-card clickable ${detailView === 'pii' ? 'active' : ''}`}
              onClick={() => setDetailView('pii')}
            >
              <div className="summary-icon pii">
                <Lock size={20} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{getPIIDetections.length}</div>
                <div className="summary-label">PII Findings</div>
              </div>
            </div>
            <div
              className={`summary-card clickable ${detailView === 'compliance' ? 'active' : ''}`}
              onClick={() => setDetailView('compliance')}
            >
              <div className="summary-icon compliance">
                <Shield size={20} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{scanDetail.total_compliance_findings}</div>
                <div className="summary-label">Compliance</div>
              </div>
            </div>
          </div>

          {/* Filter and Sort Bar */}
          <div className="detail-controls">
            <div className="view-tabs">
              <button
                className={`view-tab ${detailView === 'all' ? 'active' : ''}`}
                onClick={() => setDetailView('all')}
              >
                <Layers size={16} /> All
              </button>
              <button
                className={`view-tab ${detailView === 'repos' ? 'active' : ''}`}
                onClick={() => setDetailView('repos')}
              >
                <Database size={16} /> By Repository
              </button>
            </div>

            <div className="sort-controls">
              <label><SortDesc size={14} /> Sort:</label>
              <select
                value={detailSort}
                onChange={(e) => setDetailSort(e.target.value)}
              >
                <option value="confidence-desc">Severity (High → Low)</option>
                <option value="confidence-asc">Severity (Low → High)</option>
                <option value="type">Detection Type</option>
                <option value="repo">Repository</option>
              </select>
            </div>

            {(detailView === 'all' || detailView === 'mcp') && getDetectionTypes.length > 0 && (
              <div className="type-filters">
                <label><Filter size={14} /> Filter:</label>
                <div className="filter-chips">
                  {getDetectionTypes.map(type => (
                    <button
                      key={type}
                      className={`filter-chip ${detailFilter.includes(type) ? 'active' : ''}`}
                      onClick={() => {
                        if (detailFilter.includes(type)) {
                          setDetailFilter(detailFilter.filter(t => t !== type));
                        } else {
                          setDetailFilter([...detailFilter, type]);
                        }
                      }}
                    >
                      {type.replace(/_/g, ' ')}
                    </button>
                  ))}
                  {detailFilter.length > 0 && (
                    <button
                      className="filter-chip clear"
                      onClick={() => setDetailFilter([])}
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Content based on view */}
          <div className="detail-content">
            {/* Repository View */}
            {detailView === 'repos' && (
              <div className="repositories-section">
                <h3>Repositories ({getSortedRepos.length})</h3>
                <div className="repo-list">
                  {getSortedRepos.map((repo) => (
                    <div key={repo.full_name} className="repo-card">
                      <div
                        className="repo-header"
                        onClick={() => toggleRepoExpansion(repo.full_name)}
                      >
                        <div className="repo-expand-icon">
                          {expandedRepos[repo.full_name] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        </div>
                        <div className="repo-info">
                          <div className="repo-name">
                            <Github size={16} />
                            {repo.name}
                          </div>
                          <div className="repo-full-name">{repo.full_name}</div>
                        </div>
                        <div className="repo-badges">
                          {repo.mcp_detections_count > 0 && (
                            <span className="badge detection-badge">
                              <AlertCircle size={14} />
                              {repo.mcp_detections_count} MCP
                            </span>
                          )}
                          {repo.compliance_findings_count > 0 && (
                            <span className="badge compliance-badge">
                              <Shield size={14} />
                              {repo.compliance_findings_count} Compliance
                            </span>
                          )}
                          {repo.mcp_detections?.some(d => d.type === 'pii_handling') && (
                            <span className="badge pii-badge">
                              <Lock size={14} />
                              PII
                            </span>
                          )}
                        </div>
                      </div>

                      {expandedRepos[repo.full_name] && (
                        <div className="repo-details">
                          {repo.mcp_detections && repo.mcp_detections.length > 0 && (
                            <div className="detection-section">
                              <h5><AlertCircle size={16} /> Detections</h5>
                              {[...repo.mcp_detections].sort((a, b) => b.confidence - a.confidence).map((detection, idx) => (
                                <div key={idx} className="detection-item">
                                  <div className="detection-header">
                                    <span className="detection-type">{detection.type.replace(/_/g, ' ')}</span>
                                    <span className={`confidence-badge ${getConfidenceBadge(detection.confidence).className}`}>
                                      {getConfidenceBadge(detection.confidence).label} ({Math.round(detection.confidence * 100)}%)
                                    </span>
                                  </div>
                                  <div className="detection-file">
                                    <FileCode size={14} />
                                    <span>{detection.file_path}</span>
                                  </div>
                                  {detection.evidence && detection.evidence.length > 0 && (
                                    <div className="detection-evidence">
                                      <pre className="evidence-code">
                                        {detection.evidence.slice(0, 4).map((line, i) => (
                                          <div key={i} className="evidence-line">{line}</div>
                                        ))}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {repo.compliance_findings && repo.compliance_findings.length > 0 && (
                            <div className="compliance-section">
                              <h5><Shield size={16} /> Compliance ({repo.compliance_findings.length})</h5>
                              {[...repo.compliance_findings]
                                .sort((a, b) => b.confidence - a.confidence)
                                .slice(0, expandedRepoFindings[repo.full_name] ? undefined : 5)
                                .map((finding, idx) => (
                                <div key={idx} className="finding-item">
                                  <div className="finding-header">
                                    <span className="finding-control">{finding.control_id}</span>
                                    <span className="finding-type">{finding.type.replace(/_/g, ' ')}</span>
                                    <span className={`confidence-badge ${getConfidenceBadge(finding.confidence).className}`}>
                                      {Math.round(finding.confidence * 100)}%
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {repo.compliance_findings.length > 5 && (
                                <button
                                  className="more-findings-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRepoFindingsExpand(repo.full_name);
                                  }}
                                >
                                  {expandedRepoFindings[repo.full_name]
                                    ? 'Show Less'
                                    : `+${repo.compliance_findings.length - 5} more`
                                  }
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* MCP Detections View */}
            {detailView === 'mcp' && (
              <div className="detections-list-view">
                <h3>MCP Detections ({getFilteredDetections.length})</h3>
                {getFilteredDetections.length === 0 ? (
                  <div className="no-results">
                    <Info size={24} />
                    <p>No MCP detections found{detailFilter.length > 0 ? ' matching your filters' : ''}.</p>
                  </div>
                ) : (
                  <div className="detection-list">
                    {getFilteredDetections.map((detection, idx) => (
                      <div key={idx} className="detection-item">
                        <div className="detection-header">
                          <span className="detection-type">{detection.type.replace(/_/g, ' ')}</span>
                          <span className={`confidence-badge ${getConfidenceBadge(detection.confidence).className}`}>
                            {getConfidenceBadge(detection.confidence).label} ({Math.round(detection.confidence * 100)}%)
                          </span>
                        </div>
                        <div className="detection-meta">
                          <span className="detection-repo"><Github size={14} /> {detection.repository}</span>
                          <span className="detection-file"><FileCode size={14} /> {detection.file_path}</span>
                        </div>
                        {detection.evidence && detection.evidence.length > 0 && (
                          <div className="detection-evidence">
                            <pre className="evidence-code">
                              {detection.evidence.slice(0, 3).map((line, i) => (
                                <div key={i} className="evidence-line">{line}</div>
                              ))}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* PII View */}
            {detailView === 'pii' && (
              <div className="detections-list-view">
                <h3>PII Findings ({getPIIDetections.length})</h3>
                {getPIIDetections.length === 0 ? (
                  <div className="no-results">
                    <Lock size={24} />
                    <p>No PII handling detected in this scan.</p>
                  </div>
                ) : (
                  <div className="detection-list">
                    {[...getPIIDetections].sort((a, b) => b.confidence - a.confidence).map((detection, idx) => (
                      <div key={idx} className="detection-item pii-item">
                        <div className="detection-header">
                          <span className="detection-type"><Lock size={14} /> PII Handling</span>
                          <span className={`confidence-badge ${getConfidenceBadge(detection.confidence).className}`}>
                            {getConfidenceBadge(detection.confidence).label} ({Math.round(detection.confidence * 100)}%)
                          </span>
                        </div>
                        <div className="detection-meta">
                          <span className="detection-repo"><Github size={14} /> {detection.repository}</span>
                          <span className="detection-file"><FileCode size={14} /> {detection.file_path}</span>
                        </div>
                        {detection.evidence && detection.evidence.length > 0 && (
                          <div className="detection-evidence">
                            <pre className="evidence-code">
                              {detection.evidence.slice(0, 4).map((line, i) => (
                                <div key={i} className="evidence-line">{line}</div>
                              ))}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Compliance View */}
            {detailView === 'compliance' && (
              <div className="compliance-list-view">
                <h3>ISO 42001 Compliance Findings ({getFilteredCompliance.length})</h3>
                {getFilteredCompliance.length === 0 ? (
                  <div className="no-results">
                    <Shield size={24} />
                    <p>No compliance findings in this scan.</p>
                  </div>
                ) : (
                  <div className="compliance-list">
                    {getFilteredCompliance.slice(0, complianceLimit).map((finding, idx) => (
                      <div key={idx} className="finding-item">
                        <div className="finding-header">
                          <span className="finding-control">{finding.control_id}</span>
                          <span className="finding-type">{finding.type.replace(/_/g, ' ')}</span>
                          <span className={`status-badge ${finding.compliance_status}`}>
                            {finding.compliance_status.replace(/_/g, ' ')}
                          </span>
                          <span className={`confidence-badge ${getConfidenceBadge(finding.confidence).className}`}>
                            {Math.round(finding.confidence * 100)}%
                          </span>
                        </div>
                        <div className="finding-meta">
                          <span><Github size={14} /> {finding.repository}</span>
                          <span><FileCode size={14} /> {finding.file_path}</span>
                        </div>
                        {finding.recommendations && finding.recommendations.length > 0 && (
                          <div className="finding-recommendations">
                            <ul>
                              {finding.recommendations.slice(0, 2).map((rec, i) => (
                                <li key={i}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                    {getFilteredCompliance.length > complianceLimit && (
                      <div className="pagination-controls">
                        <span className="pagination-info">
                          Showing {Math.min(complianceLimit, getFilteredCompliance.length)} of {getFilteredCompliance.length} findings
                        </span>
                        <button
                          className="show-more-btn"
                          onClick={() => setComplianceLimit(prev => prev + 50)}
                        >
                          Show More (+50)
                        </button>
                        <button
                          className="show-all-btn"
                          onClick={() => setComplianceLimit(getFilteredCompliance.length)}
                        >
                          Show All
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* All Detections View */}
            {detailView === 'all' && (
              <div className="all-detections-view">
                <h3>All Detections ({getFilteredDetections.length + getFilteredCompliance.length})</h3>

                {getFilteredDetections.length > 0 && (
                  <div className="section-group">
                    <h4><AlertCircle size={18} /> MCP & PII Detections ({getFilteredDetections.length})</h4>
                    <div className="detection-list">
                      {getFilteredDetections.slice(0, 20).map((detection, idx) => (
                        <div key={idx} className={`detection-item ${detection.type === 'pii_handling' ? 'pii-item' : ''}`}>
                          <div className="detection-header">
                            <span className="detection-type">
                              {detection.type === 'pii_handling' ? <Lock size={14} /> : <Code size={14} />}
                              {detection.type.replace(/_/g, ' ')}
                            </span>
                            <span className={`confidence-badge ${getConfidenceBadge(detection.confidence).className}`}>
                              {getConfidenceBadge(detection.confidence).label} ({Math.round(detection.confidence * 100)}%)
                            </span>
                          </div>
                          <div className="detection-meta">
                            <span><Github size={14} /> {detection.repository.split('/')[1]}</span>
                            <span><FileCode size={14} /> {detection.file_path}</span>
                          </div>
                        </div>
                      ))}
                      {getFilteredDetections.length > 20 && (
                        <button className="show-more-btn" onClick={() => setDetailView('mcp')}>
                          View all {getFilteredDetections.length} detections →
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {getFilteredCompliance.length > 0 && (
                  <div className="section-group">
                    <h4><Shield size={18} /> Compliance Findings ({getFilteredCompliance.length})</h4>
                    <div className="compliance-list">
                      {getFilteredCompliance.slice(0, 10).map((finding, idx) => (
                        <div key={idx} className="finding-item compact">
                          <div className="finding-header">
                            <span className="finding-control">{finding.control_id}</span>
                            <span className="finding-type">{finding.type.replace(/_/g, ' ')}</span>
                            <span className={`confidence-badge ${getConfidenceBadge(finding.confidence).className}`}>
                              {Math.round(finding.confidence * 100)}%
                            </span>
                          </div>
                          <div className="finding-meta">
                            <span><Github size={14} /> {finding.repository.split('/')[1]}</span>
                          </div>
                        </div>
                      ))}
                      {getFilteredCompliance.length > 10 && (
                        <button className="show-more-btn" onClick={() => setDetailView('compliance')}>
                          View all {getFilteredCompliance.length} findings →
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="github-scanner-dashboard">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <Github size={32} color="#16a085" />
          <div>
            <h2>GitHub Organization Scanner</h2>
            <p>Automated MCP Development Detection & ISO 42001 Compliance Scanning</p>
          </div>
        </div>
        <button
          className="github-scanner-btn primary"
          onClick={() => onNavigateToProfile('github')}
        >
          <Plus size={20} />
          Manage Organizations
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <Settings size={24} color="#3498db" />
          </div>
          <div className="stat-content">
            <div className="stat-number">{githubOrgs.length}</div>
            <div className="stat-label">GitHub Organizations</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <BarChart3 size={24} color="#e74c3c" />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {scanResults.reduce((sum, result) => sum + result.detections_count, 0)}
            </div>
            <div className="stat-label">Total MCP Detections</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Shield size={24} color="#f39c12" />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {scanResults.length > 0
                ? Math.round(scanResults.reduce((sum, result) => sum + (result.overall_compliance_score || 0), 0) / scanResults.length)
                : 0}%
            </div>
            <div className="stat-label">Avg Compliance Score</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Clock size={24} color="#16a085" />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {scanJobs.filter(job => job.status === 'completed').length}
            </div>
            <div className="stat-label">Completed Scans</div>
          </div>
        </div>
      </div>

      {/* GitHub Organizations */}
      <div className="dashboard-section">
        <div className="section-header">
          <h3>GitHub Organizations</h3>
          <button
            className="github-scanner-btn secondary"
            onClick={() => onNavigateToProfile('github')}
          >
            <Settings size={16} />
            Manage Organizations
          </button>
        </div>
        <div className="scan-configs">
          {githubOrgs.length === 0 ? (
            <div className="empty-state">
              <Github size={48} color="#bdc3c7" />
              <h4>No GitHub Organizations Configured</h4>
              <p>Add your first GitHub organization to start automated MCP detection and compliance scanning.</p>
              <button
                className="github-scanner-btn primary"
                onClick={() => onNavigateToProfile('github')}
              >
                <Plus size={20} />
                Add Organization
              </button>
            </div>
          ) : (
            <div className="config-grid">
              {githubOrgs.map((org) => (
                <div key={org.id} className="config-card">
                  <div className="config-header">
                    <h4>{org.name}</h4>
                    <div className="config-actions">
                      <button
                        className="action-btn"
                        onClick={() => handleManualScan(org.url)}
                        title="Run manual scan"
                      >
                        <Play size={16} />
                      </button>
                      <button
                        className="action-btn"
                        onClick={() => {
                          setSelectedOrg(extractOrgNameFromUrl(org.url));
                          setActiveTab('results');
                          loadScanResults(extractOrgNameFromUrl(org.url));
                        }}
                        title="View results"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="action-btn"
                        onClick={() => onNavigateToProfile('github')}
                        title="Manage organization"
                      >
                        <Settings size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="config-details">
                    <div className="config-detail">
                      <span className="label">URL:</span>
                      <span className="value org-url" title={org.url}>
                        {org.url.length > 40 ? `${org.url.substring(0, 37)}...` : org.url}
                      </span>
                    </div>
                    <div className="config-detail">
                      <span className="label">Frequency:</span>
                      <span className="value">{org.scan_frequency}</span>
                    </div>
                    <div className="config-detail">
                      <span className="label">Max Repos:</span>
                      <span className="value">{org.max_repositories}</span>
                    </div>
                    <div className="config-detail">
                      <span className="label">Last Scan:</span>
                      <span className="value">
                        {org.last_scan_at
                          ? new Date(org.last_scan_at).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                    </div>
                  </div>
                  <div className={`config-status ${org.is_active ? 'active' : 'inactive'}`}>
                    {org.is_active ? 'Active' : 'Inactive'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Jobs */}
      <div className="dashboard-section">
        <h3>Recent Scan Jobs</h3>
        <div className="job-list">
          {scanJobs.slice(0, 5).map((job) => (
            <div
              key={job.job_id || job.config_id + job.created_at}
              className={`job-item ${job.status === 'completed' && job.results?.scan_id ? 'clickable' : ''}`}
              onClick={() => {
                if (job.status === 'completed' && job.results?.scan_id) {
                  openDetailModal(job.results.scan_id);
                }
              }}
              title={job.status === 'completed' && job.results?.scan_id ? 'Click to view details' : ''}
            >
              <div className="job-status">
                {getStatusIcon(job.status)}
              </div>
              <div className="job-info">
                <div className="job-org">{job.organization || job.github_repo?.split('/').pop()}</div>
                <div className="job-details">
                  <span>Started: {new Date(job.created_at).toLocaleString()}</span>
                  {job.results?.detections_count > 0 && (
                    <span className="detection-count">{job.results.detections_count} detections</span>
                  )}
                  {job.results?.repositories_scanned > 0 && (
                    <span>{job.results.repositories_scanned} repos</span>
                  )}
                </div>
              </div>
              <div className="job-actions">
                {job.error && (
                  <div className="job-error" title={job.error}>
                    <AlertTriangle size={16} />
                  </div>
                )}
                {job.status === 'completed' && job.results?.scan_id && (
                  <div className="view-hint">
                    <Eye size={14} />
                  </div>
                )}
              </div>
            </div>
          ))}
          {scanJobs.length === 0 && (
            <div className="empty-state-small">
              <p>No scan jobs yet. Configure an organization to start scanning.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );


  const renderResults = () => (
    <div className="github-scanner-results">
      <div className="results-header">
        <h3>Scan Results</h3>
        <div className="org-selector">
          <select
            value={selectedOrg}
            onChange={(e) => {
              setSelectedOrg(e.target.value);
              loadScanResults(e.target.value);
            }}
          >
            <option value="">Select Organization</option>
            {githubOrgs.map((org) => (
              <option key={org.id} value={extractOrgNameFromUrl(org.url)}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedOrg && (
        <div className="results-content">
          {scanResults.length === 0 ? (
            <div className="empty-state">
              <BarChart3 size={48} color="#bdc3c7" />
              <h4>No Scan Results</h4>
              <p>No scans have been completed for {selectedOrg} yet.</p>
            </div>
          ) : (
            <div className="results-grid">
              {scanResults.map((result) => (
                <div
                  key={result.scan_id}
                  className="result-card clickable"
                  onClick={() => openDetailModal(result.scan_id)}
                  title="Click to view detailed results"
                >
                  <div className="result-header">
                    <h4>{result.organization}</h4>
                    <div className="result-date">
                      {new Date(result.started_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="result-stats">
                    <div className="result-stat">
                      <span className="stat-label">Repositories:</span>
                      <span className="stat-value">{result.repositories_scanned}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">MCP Detections:</span>
                      <span className="stat-value highlight-detection">{result.detections_count}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">Compliance Findings:</span>
                      <span className="stat-value highlight-compliance">{result.compliance_findings_count}</span>
                    </div>
                    {result.overall_compliance_score && (
                      <div className="result-stat">
                        <span className="stat-label">Compliance Score:</span>
                        <span className="stat-value">{result.overall_compliance_score}%</span>
                      </div>
                    )}
                  </div>
                  <div className="result-footer">
                    <div className={`result-status ${result.scan_status}`}>
                      {result.scan_status}
                    </div>
                    <div className="view-details-hint">
                      <Eye size={14} /> Click to view details
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="github-scanner">
      {/* Tab Navigation */}
      <div className="scanner-tabs">
        <button
          className={`scanner-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          <Settings size={20} />
          Dashboard
        </button>
        <button
          className={`scanner-tab ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <BarChart3 size={20} />
          Results
        </button>
      </div>

      {/* Tab Content */}
      <div className="scanner-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'results' && renderResults()}
      </div>

      {/* Scan Detail Modal */}
      {renderDetailModal()}

      {/* Export Modal */}
      {showExportModal && scanDetail && (
        <div className="modal-overlay export-modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="export-modal-header">
              <div className="export-title">
                <FileText size={24} />
                <h3>Export PDF Report</h3>
              </div>
              <button className="modal-close" onClick={() => setShowExportModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="export-modal-content">
              <p className="export-description">
                Select the items you want to include in your PDF report:
              </p>

              {/* Repositories Selection */}
              <div className="export-section">
                <div className="export-section-header">
                  <h4>
                    <GitBranch size={18} />
                    Repositories ({Object.values(exportSelections.repos).filter(v => v).length} selected)
                  </h4>
                  <button
                    className="select-toggle-btn"
                    onClick={() => {
                      const newValue = !selectAllRepos;
                      setSelectAllRepos(newValue);
                      toggleAllInCategory('repos', newValue);
                    }}
                  >
                    {selectAllRepos ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="export-items-list">
                  {scanDetail.repositories?.map((repo) => (
                    <label key={repo.full_name} className="export-item">
                      <input
                        type="checkbox"
                        checked={exportSelections.repos[repo.full_name] || false}
                        onChange={() => toggleSelection('repos', repo.full_name)}
                      />
                      <span className="checkbox-custom">
                        {exportSelections.repos[repo.full_name] ? <CheckSquare size={18} /> : <Square size={18} />}
                      </span>
                      <span className="item-name">{repo.name}</span>
                      <span className="item-stats">
                        {repo.mcp_detections_count > 0 && <span className="stat-badge mcp">{repo.mcp_detections_count} MCP</span>}
                        {repo.compliance_findings_count > 0 && <span className="stat-badge compliance">{repo.compliance_findings_count} Compliance</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Detections Selection */}
              <div className="export-section">
                <div className="export-section-header">
                  <h4>
                    <AlertCircle size={18} />
                    MCP Detections ({Object.values(exportSelections.detections).filter(v => v).length} selected)
                  </h4>
                  <button
                    className="select-toggle-btn"
                    onClick={() => {
                      const newValue = !selectAllDetections;
                      setSelectAllDetections(newValue);
                      toggleAllInCategory('detections', newValue);
                    }}
                  >
                    {selectAllDetections ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="export-items-list detections-list">
                  {getAllDetections.slice(0, 30).map((detection, idx) => {
                    const key = `${detection.repository}-${scanDetail.repositories?.find(r => r.full_name === detection.repository)?.mcp_detections?.indexOf(detection) ?? idx}`;
                    return (
                      <label key={key} className="export-item">
                        <input
                          type="checkbox"
                          checked={exportSelections.detections[key] || false}
                          onChange={() => toggleSelection('detections', key)}
                        />
                        <span className="checkbox-custom">
                          {exportSelections.detections[key] ? <CheckSquare size={18} /> : <Square size={18} />}
                        </span>
                        <span className="item-name">{detection.type.replace(/_/g, ' ')}</span>
                        <span className="item-detail">{detection.repository.split('/')[1]}</span>
                        <span className={`confidence-badge small ${getConfidenceBadge(detection.confidence).className}`}>
                          {getConfidenceBadge(detection.confidence).label}
                        </span>
                      </label>
                    );
                  })}
                  {getAllDetections.length > 30 && (
                    <div className="export-note">
                      Showing 30 of {getAllDetections.length} detections. All selected items will be included in PDF.
                    </div>
                  )}
                </div>
              </div>

              {/* Compliance Selection */}
              <div className="export-section">
                <div className="export-section-header">
                  <h4>
                    <Shield size={18} />
                    Compliance Findings ({Object.values(exportSelections.compliance).filter(v => v).length} selected)
                  </h4>
                  <button
                    className="select-toggle-btn"
                    onClick={() => {
                      const newValue = !selectAllCompliance;
                      setSelectAllCompliance(newValue);
                      toggleAllInCategory('compliance', newValue);
                    }}
                  >
                    {selectAllCompliance ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="export-items-list compliance-list">
                  {getAllCompliance.slice(0, 30).map((finding, idx) => {
                    const key = `${finding.repository}-${scanDetail.repositories?.find(r => r.full_name === finding.repository)?.compliance_findings?.indexOf(finding) ?? idx}`;
                    return (
                      <label key={key} className="export-item">
                        <input
                          type="checkbox"
                          checked={exportSelections.compliance[key] || false}
                          onChange={() => toggleSelection('compliance', key)}
                        />
                        <span className="checkbox-custom">
                          {exportSelections.compliance[key] ? <CheckSquare size={18} /> : <Square size={18} />}
                        </span>
                        <span className="item-name">{finding.control_id}</span>
                        <span className="item-detail">{finding.type.replace(/_/g, ' ')}</span>
                      </label>
                    );
                  })}
                  {getAllCompliance.length > 30 && (
                    <div className="export-note">
                      Showing 30 of {getAllCompliance.length} findings. All selected items will be included in PDF.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="export-modal-footer">
              <button className="cancel-btn" onClick={() => setShowExportModal(false)}>
                Cancel
              </button>
              <button className="generate-btn" onClick={generatePDFReport}>
                <Download size={18} />
                Generate PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for detail fetch */}
      {loadingDetail && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading scan details...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GitHubScanner;